import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery as useConvexQuery, useAction } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'
import { ErrorBoundary } from '@/components/ErrorBoundary'

export const Route = createFileRoute('/admin/timestamp-processing')({
  component: () => (
    <ErrorBoundary>
      <TimestampProcessingPage />
    </ErrorBoundary>
  ),
})

function TimestampProcessingPage() {
  const [processing, setProcessing] = useState(false)
  const [currentSongId, setCurrentSongId] = useState<Id<'songs'> | null>(null)

  // Get all songs with processing status
  const songs = useConvexQuery(api.songs.getSongsWithProcessingStatus)
  const extractFlashcardAudio = useAction(api.assemblyai.processGenerationWithAssemblyAI)

  const handleProcessSong = async (songId: Id<'songs'>, generationId: Id<'temporGenerations'>) => {
    if (processing) return

    setProcessing(true)
    setCurrentSongId(songId)

    try {
      await extractFlashcardAudio({
        generationId,
      })
    } catch (error) {
      console.error('Failed to process song:', error)
      alert(`Failed to process song: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setProcessing(false)
      setCurrentSongId(null)
    }
  }

  const handleProcessAll = async () => {
    if (processing) return

    const songsToProcess = songs?.filter(
      (song) =>
        song.audioGeneration &&
        song.processingStatus !== 'completed' &&
        song.processingStatus !== 'failed'
    )

    if (!songsToProcess || songsToProcess.length === 0) {
      alert('No songs to process!')
      return
    }

    if (!confirm(`Process ${songsToProcess.length} songs? This may take several minutes.`)) {
      return
    }

    setProcessing(true)
    let successCount = 0
    let failureCount = 0
    const failures: Array<{ title: string; error: string }> = []

    for (const song of songsToProcess) {
      if (song.audioGeneration) {
        try {
          setCurrentSongId(song._id)
          await extractFlashcardAudio({
            generationId: song.audioGeneration._id,
          })
          successCount++
        } catch (error) {
          failureCount++
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          failures.push({ title: song.title, error: errorMessage })
          console.error(`[Batch] Failed to process "${song.title}":`, error)
          // Continue to next song instead of stopping
        }

        // Add delay between songs to avoid overloading WhisperX service
        await new Promise((resolve) => setTimeout(resolve, 2000))
      }
    }

    setProcessing(false)
    setCurrentSongId(null)

    // Show comprehensive results
    if (failureCount === 0) {
      alert(`✅ Successfully processed all ${successCount} songs!`)
    } else {
      const failureDetails = failures.map(f => `• ${f.title}: ${f.error}`).join('\n')
      alert(
        `⚠️ Batch processing completed with errors:\n\n` +
        `Success: ${successCount} songs\n` +
        `Failed: ${failureCount} songs\n\n` +
        `Failed songs:\n${failureDetails}`
      )
    }
  }

  if (!songs) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading songs...</div>
      </div>
    )
  }

  const stats = {
    total: songs.length,
    notStarted: songs.filter((s) => s.processingStatus === 'not_started').length,
    inProgress: songs.filter((s) => s.processingStatus === 'in_progress').length,
    needsReview: songs.filter((s) => s.processingStatus === 'needs_review').length,
    completed: songs.filter((s) => s.processingStatus === 'completed').length,
    failed: songs.filter((s) => s.processingStatus === 'failed').length,
  }

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="terminal-border p-6 mb-6">
          <h1 className="text-3xl font-bold text-terminal-green font-mono mb-4">
            [TIMESTAMP BATCH PROCESSING]
          </h1>
          <p className="text-trail-tan font-mono mb-6">
            Process Spanish verb songs to extract precise audio timestamps for flashcards
          </p>

          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-gray-900 p-4 border-2 border-gray-700">
              <div className="text-xs text-trail-tan font-mono mb-1">TOTAL</div>
              <div className="text-2xl text-white font-mono">{stats.total}</div>
            </div>
            <div className="bg-gray-900 p-4 border-2 border-yellow-600">
              <div className="text-xs text-trail-tan font-mono mb-1">NOT STARTED</div>
              <div className="text-2xl text-yellow-500 font-mono">{stats.notStarted}</div>
            </div>
            <div className="bg-gray-900 p-4 border-2 border-blue-600">
              <div className="text-xs text-trail-tan font-mono mb-1">IN PROGRESS</div>
              <div className="text-2xl text-sky-blue font-mono">{stats.inProgress}</div>
            </div>
            <div className="bg-gray-900 p-4 border-2 border-orange-600">
              <div className="text-xs text-trail-tan font-mono mb-1">NEEDS REVIEW</div>
              <div className="text-2xl text-orange-500 font-mono">{stats.needsReview}</div>
            </div>
            <div className="bg-gray-900 p-4 border-2 border-terminal-green">
              <div className="text-xs text-trail-tan font-mono mb-1">COMPLETED</div>
              <div className="text-2xl text-terminal-green font-mono">{stats.completed}</div>
            </div>
            <div className="bg-gray-900 p-4 border-2 border-red-600">
              <div className="text-xs text-trail-tan font-mono mb-1">FAILED</div>
              <div className="text-2xl text-red-500 font-mono">{stats.failed}</div>
            </div>
          </div>

          {/* Batch Action Button */}
          <button
            onClick={handleProcessAll}
            disabled={processing || stats.notStarted + stats.inProgress === 0}
            className="w-full bg-terminal-green text-black font-bold font-mono py-4 px-6
                     hover:bg-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? '[PROCESSING...]' : `[PROCESS ALL (${stats.notStarted + stats.inProgress} SONGS)]`}
          </button>
        </div>

        {/* Song List */}
        <div className="space-y-4">
          {songs.map((song) => (
            <div key={song._id} className="terminal-border p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-sky-blue font-mono">{song.title}</h2>
                  <p className="text-trail-tan font-mono text-sm">Verb: {song.targetVerb}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={song.processingStatus} />
                  {song.averageConfidence !== undefined && (
                    <div className="text-xs text-trail-tan font-mono mt-2">
                      Confidence: {(song.averageConfidence * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
              </div>

              {/* Flashcard Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm font-mono mb-2">
                  <span className="text-trail-tan">
                    Flashcards: {song.flashcardStats.withAudio}/{song.flashcardStats.total}
                  </span>
                  <span className="text-sky-blue">{song.flashcardStats.percentComplete}%</span>
                </div>
                <div className="w-full bg-gray-800 h-2">
                  <div
                    className="bg-terminal-green h-2 transition-all"
                    style={{ width: `${song.flashcardStats.percentComplete}%` }}
                  />
                </div>
                {song.flashcardStats.needingReview > 0 && (
                  <div className="text-xs text-yellow-500 font-mono mt-2">
                    {song.flashcardStats.needingReview} flashcard(s) need manual review
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                {song.audioGeneration && song.processingStatus !== 'completed' && (
                  <button
                    onClick={() => handleProcessSong(song._id, song.audioGeneration!._id)}
                    disabled={processing && currentSongId !== song._id}
                    className="flex-1 bg-sky-blue text-black font-bold font-mono py-3 px-6
                             hover:bg-blue-400 transition-colors disabled:opacity-50"
                  >
                    {processing && currentSongId === song._id
                      ? '[PROCESSING...]'
                      : '[PROCESS SONG]'}
                  </button>
                )}
                {song.flashcardStats.needingReview > 0 && (
                  <a
                    href={`/app/audio-review?songId=${song._id}`}
                    className="flex-1 bg-yellow-500 text-black font-bold font-mono py-3 px-6
                             hover:bg-yellow-400 transition-colors text-center"
                  >
                    [REVIEW FLAGGED]
                  </a>
                )}
                {!song.audioGeneration && (
                  <div className="flex-1 text-trail-tan font-mono py-3 px-6 text-center border-2 border-gray-700">
                    [NO AUDIO FILE]
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Instructions */}
        <div className="terminal-border p-6 mt-6">
          <h3 className="text-lg font-bold text-terminal-green font-mono mb-4">
            [INSTRUCTIONS]
          </h3>
          <ul className="text-trail-tan font-mono text-sm space-y-2">
            <li>• Click [PROCESS ALL] to batch process all songs with audio files</li>
            <li>• Click [PROCESS SONG] to process an individual song</li>
            <li>• Click [REVIEW FLAGGED] to manually adjust low-confidence timestamps</li>
            <li>• WhisperX will extract word-level timestamps with 70-85%+ accuracy</li>
            <li>• Processing time: ~30-60 seconds per song</li>
            <li>• Bilingual songs may require manual review for best results</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({
  status,
}: {
  status: 'not_started' | 'in_progress' | 'completed' | 'needs_review' | 'failed'
}) {
  const colors = {
    not_started: 'bg-gray-700 text-gray-300',
    in_progress: 'bg-blue-600 text-white',
    needs_review: 'bg-orange-600 text-white',
    completed: 'bg-green-600 text-white',
    failed: 'bg-red-600 text-white',
  }

  const labels = {
    not_started: 'NOT STARTED',
    in_progress: 'IN PROGRESS',
    needs_review: 'NEEDS REVIEW',
    completed: 'COMPLETED',
    failed: 'FAILED',
  }

  return (
    <div className={`inline-block px-3 py-1 font-mono text-xs font-bold ${colors[status]}`}>
      [{labels[status]}]
    </div>
  )
}
